/**
 * Runtime configuration loader.
 *
 * The config is fetched from `/config.json` at runtime (served by S3),
 * NOT bundled into the application. This allows different deployments
 * (Production vs Homologation) to use the same build artifact with
 * different configuration.
 */

export interface RuntimeConfig {
  /** Domain of this deployment (e.g. "irondome.lcopay.com") */
  domain: string;
  /** Managed environments this deployment is authorized to operate */
  authorizedEnvironments: string[];
  /** Azure AD App Registration client ID for MSAL */
  clientId: string;
  /** Azure AD authority URL (issuer) */
  authority: string;
  /** Base URL of the Irondome BFF API */
  apiBaseUrl: string;
}

type RuntimeConfigGlobal = typeof globalThis & {
  __irondomeRuntimeConfig?: RuntimeConfig;
};

const runtimeConfigGlobal = globalThis as RuntimeConfigGlobal;

/**
 * Loads the runtime configuration from `/config.json`.
 * Must be called before the app renders. Caches the result
 * for subsequent calls.
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (runtimeConfigGlobal.__irondomeRuntimeConfig) {
    return runtimeConfigGlobal.__irondomeRuntimeConfig;
  }
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config: ${response.status} ${response.statusText}`,
    );
  }
  const config = await response.json() as RuntimeConfig;
  runtimeConfigGlobal.__irondomeRuntimeConfig = config;
  return config;
}

/**
 * Returns the cached runtime configuration.
 * Throws if `loadConfig()` has not been called yet.
 */
export function getConfig(): RuntimeConfig {
  const config = runtimeConfigGlobal.__irondomeRuntimeConfig;
  if (!config) {
    throw new Error('Config not loaded. Call loadConfig() first.');
  }
  return config;
}
